import React, { useEffect, useMemo, useState } from 'react'
import 'antd/dist/antd.css'
import { Input, Typography } from 'antd'
import Select from 'react-select'
import { useICD10 } from 'react-icd10'
import { useTranslation } from 'react-i18next'
import PatientDiagnosisRepository from 'vdg-patients/appointment/PatientDiagnosisRepository'

const { TextArea } = Input
const { Text } = Typography

/**
 * One ICD-10 code an order is billed under. `diagnosisId` is set when it is one of the patient's
 * diagnoses (from PCC); a code picked from the full ICD-10 list has none and stays on the order only.
 */
export type OrderDiagnosis = {
  diagnosisCode: string
  diagnosisId?: string
  diagnosisName?: string
}

/** The catch-all every patient carries. It anchors an order to the chart; it is not a reason. */
export const GENERAL_DIAGNOSIS_CODE = 'Z00.8'

/** The patient's diagnoses offered as quick picks: active ones, without the catch-all Z00.8. */
export const quickPickDiagnoses = (diagnoses: any[] | undefined): OrderDiagnosis[] =>
  (diagnoses ?? [])
    .filter(d => d?.diagnosisCode && d.diagnosisCode !== GENERAL_DIAGNOSIS_CODE)
    .filter(d => d.active !== false)
    .map(d => ({
      diagnosisCode: d.diagnosisCode,
      diagnosisId: d._id ?? d.diagnosisId,
      diagnosisName: d.diagnosisName,
    }))

/**
 * An order's codes without the Z00.8 the server adds to anchor codes picked from the full ICD-10
 * list. Kept when it is the only code: then it was the choice.
 */
export const withoutAnchor = <T extends { diagnosisCode: string }>(diagnoses: T[]): T[] =>
  diagnoses.some(d => d.diagnosisCode !== GENERAL_DIAGNOSIS_CODE)
    ? diagnoses.filter(d => d.diagnosisCode !== GENERAL_DIAGNOSIS_CODE)
    : diagnoses

/** Both parts of the reason are required on every order a clinician places. */
export const isOrderReasonComplete = (
  indication: string | undefined,
  diagnoses: OrderDiagnosis[],
) => !!indication?.trim() && diagnoses.length > 0

/** The patient's diagnosis list, for screens that do not have it in the store. */
export const usePatientDiagnosesList = (patientId: string | undefined) => {
  const [diagnoses, setDiagnoses] = useState<any[]>([])

  useEffect(() => {
    if (!patientId) return
    let cancelled = false
    PatientDiagnosisRepository.findAllPatientDiagnosis({ patientId })
      .then(list => !cancelled && setDiagnoses(list ?? []))
      .catch(err => console.error(err))
    return () => {
      cancelled = true
    }
  }, [patientId])

  return diagnoses
}

const diagnosisLabel = (d: OrderDiagnosis) =>
  d.diagnosisName ? `${d.diagnosisCode} ${d.diagnosisName}` : d.diagnosisCode

type DiagnosisOption = {
  label: string
  value: string
  diagnosis: OrderDiagnosis
  fromSearch?: boolean
}

const toOption = (diagnosis: OrderDiagnosis): DiagnosisOption => ({
  label: diagnosisLabel(diagnosis),
  value: diagnosis.diagnosisCode,
  diagnosis,
})

type DiagnosisPickerProps = {
  value: OrderDiagnosis[]
  onChange: (diagnoses: OrderDiagnosis[]) => void
  /** The patient's diagnoses; omitted (group orders) leaves only the ICD-10 search. */
  patientDiagnoses?: any[]
  disabled?: boolean
}

/**
 * One multi-select: the patient's diagnoses as quick picks, and every ICD-10 code by typing.
 * Codes are searched with react-icd10, as the result screen's ICD-10 field does.
 */
export const DiagnosisPicker = (props: DiagnosisPickerProps) => {
  const { value, onChange, patientDiagnoses, disabled } = props
  const { t } = useTranslation()
  const { onSearch, data } = useICD10({ limit: 50 })
  const [input, setInput] = useState('')

  const quickPicks = useMemo(() => quickPickDiagnoses(patientDiagnoses).map(toOption), [
    patientDiagnoses,
  ])

  // Typed as objects, but react-icd10 fills `results` with code -> name strings (see Icd10Search).
  const results = (data?.results ?? {}) as Record<string, any>
  const searchResults: DiagnosisOption[] = input.trim()
    ? Object.keys(results).map(code => ({
        ...toOption({ diagnosisCode: code, diagnosisName: String(results[code]) }),
        fromSearch: true,
      }))
    : []

  const options = [
    ...(quickPicks.length > 0
      ? [{ label: t('orderReason.patientDiagnoses'), options: quickPicks }]
      : []),
    ...(searchResults.length > 0
      ? [{ label: t('orderReason.allCodes'), options: searchResults }]
      : []),
  ]

  return (
    <Select
      isMulti
      isDisabled={disabled}
      placeholder={t('orderReason.diagnosesPlaceholder')}
      value={value.map(toOption)}
      options={options as any}
      inputValue={input}
      onInputChange={(text: string, meta: any) => {
        // react-select clears the text on blur and after a pick; only typing searches.
        if (meta?.action !== 'input-change') {
          setInput('')
          return
        }
        setInput(text)
        onSearch(text)
      }}
      // Quick picks narrow to what is typed; search results are already the answer to it.
      filterOption={(option: any, typed: string) =>
        option.data.fromSearch || option.label.toLowerCase().includes(typed.toLowerCase())
      }
      noOptionsMessage={() => t('orderReason.typeToSearch')}
      onChange={(selected: any) =>
        onChange((selected ?? []).map((option: DiagnosisOption) => option.diagnosis))
      }
      // Rendered on the body so a modal's edges do not clip it, and above every antd modal: those
      // sit at z-index 9000 app-wide (SupportTicket.scss), not antd's own 1000.
      menuPortalTarget={document.body}
      styles={{ menuPortal: (base: any) => ({ ...base, zIndex: 9100 }) }}
    />
  )
}

type OrderReasonFieldsProps = {
  indication: string
  onIndicationChange: (indication: string) => void
  diagnoses: OrderDiagnosis[]
  onDiagnosesChange: (diagnoses: OrderDiagnosis[]) => void
  patientDiagnoses?: any[]
  disabled?: boolean
  /** Shown under the reason label, e.g. that the text came from the AI. */
  hint?: string
  /** Shown under the ICD-10 label, e.g. that the codes came from the AI. */
  diagnosesHint?: string
}

/** The reason for a test: what the interpreter and the progress note read, and what billing codes. */
const OrderReasonFields = (props: OrderReasonFieldsProps) => {
  const { t } = useTranslation()

  return (
    <div className="orderReasonFields">
      <div style={{ marginBottom: 10 }}>
        <Text strong>
          {t('orderReason.indication')} <span style={{ color: 'red' }}>*</span>
        </Text>
        <div className="actionReportMuted" style={{ fontSize: 12 }}>
          {props.hint ?? t('orderReason.indicationHint')}
        </div>
        <TextArea
          style={{ marginTop: 4 }}
          autoSize={{ minRows: 2 }}
          disabled={props.disabled}
          placeholder={t('orderReason.indicationPlaceholder')}
          value={props.indication}
          onChange={e => props.onIndicationChange(e.target.value)}
        />
      </div>
      <div>
        <Text strong>
          {t('orderReason.diagnoses')} <span style={{ color: 'red' }}>*</span>
        </Text>
        {props.diagnosesHint && (
          <div className="actionReportMuted" style={{ fontSize: 12 }}>
            {props.diagnosesHint}
          </div>
        )}
        <div style={{ marginTop: 4 }}>
          <DiagnosisPicker
            value={props.diagnoses}
            onChange={props.onDiagnosesChange}
            patientDiagnoses={props.patientDiagnoses}
            disabled={props.disabled}
          />
        </div>
      </div>
    </div>
  )
}

export default OrderReasonFields
